import { BasePage } from '@/components/ui/base-page'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useState } from 'react'

// Define the article type
type ArticleCategory = 'Opinion' | 'Partner Insights' | 'Investment Announcements'

interface Article {
  id: string
  slug: string
  title: string
  excerpt: string
  coverImage: string
  date: string
  author: {
    name: string
    avatar: string
  }
  category: ArticleCategory
  tags: string[]
}

// Sample articles data
const articles: Article[] = [
  {
    id: '1',
    slug: 'the-future-of-fintech',
    title: 'The Future of Fintech in 2024',
    excerpt: 'Exploring emerging trends in financial technology and what to expect in the coming year.',
    coverImage: 'https://images.unsplash.com/photo-1620714223084-8fcacc6dfd8d',
    date: 'April 12, 2024',
    author: {
      name: 'Sarah Johnson',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
    },
    category: 'Opinion',
    tags: ['Fintech', 'Innovation', 'Trends']
  },
  {
    id: '2',
    slug: 'sustainable-investment-strategies',
    title: 'Sustainable Investment Strategies for Long-term Growth',
    excerpt: 'How ESG factors are reshaping portfolio management and creating new opportunities.',
    coverImage: 'https://images.unsplash.com/photo-1579532537598-459ecdaf39cc',
    date: 'April 5, 2024',
    author: {
      name: 'Michael Chen',
      avatar: 'https://randomuser.me/api/portraits/men/22.jpg'
    },
    category: 'Investment Announcements',
    tags: ['ESG', 'Sustainable', 'Growth']
  },
  {
    id: '3',
    slug: 'ai-revolutionizing-venture-capital',
    title: 'How AI is Revolutionizing Venture Capital',
    excerpt: 'The impact of artificial intelligence on investment decisions and startup evaluation.',
    coverImage: 'https://images.unsplash.com/photo-1555774698-0b77e0d5fac6',
    date: 'March 28, 2024',
    author: {
      name: 'Elena Rodriguez',
      avatar: 'https://randomuser.me/api/portraits/women/29.jpg'
    },
    category: 'Partner Insights',
    tags: ['AI', 'VC', 'Technology']
  },
  {
    id: '4',
    slug: 'crypto-regulations-2024',
    title: 'Navigating Crypto Regulations in 2024',
    excerpt: 'Understanding the evolving regulatory landscape for cryptocurrency investments.',
    coverImage: 'https://images.unsplash.com/photo-1639762681057-408e52192e55',
    date: 'March 21, 2024',
    author: {
      name: 'David Park',
      avatar: 'https://randomuser.me/api/portraits/men/77.jpg'
    },
    category: 'Opinion',
    tags: ['Crypto', 'Regulation', 'Compliance']
  }
]

// Article Card Component
const ArticleCard = ({ article }: { article: Article }) => {
  const [isHovering, setIsHovering] = useState(false)

  return (
    <Link to={`/article/${article.slug}`} className="block">
      <Card
        className={`h-full overflow-hidden transition-all duration-300 ${isHovering ? 'transform scale-[1.03] shadow-xl' : 'shadow-md'
          }`}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div className="relative h-48 w-full overflow-hidden">
          <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 transition-opacity duration-300 ${isHovering ? 'opacity-70' : 'opacity-40'
            }`} />
          <div
            className={`relative w-full h-full bg-cover bg-center transition-transform duration-500 ${isHovering ? 'scale-110' : 'scale-100'
              }`}
            style={{ backgroundImage: `url(${article.coverImage})` }}
          >
            {isHovering && (
              <div className="absolute inset-0 animate-pulse bg-white/10 z-10"></div>
            )}
            <div className="absolute bottom-4 left-4 z-20">
              <span className="inline-block bg-white text-black text-xs font-semibold px-2 py-1 rounded">
                {article.category}
              </span>
            </div>
          </div>
        </div>

        <CardContent className="pt-4">
          <div className="mb-2 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full overflow-hidden">
              <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${article.author.avatar})` }} />
            </div>
            <span className="text-sm text-gray-600">{article.author.name}</span>
            <span className="text-xs text-gray-400">•</span>
            <span className="text-sm text-gray-600">{article.date}</span>
          </div>
          <h3 className={`font-bold mb-2 transition-all duration-300 ${isHovering ? 'text-blue-600' : 'text-gray-900'
            }`}>
            {article.title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-2">{article.excerpt}</p>
        </CardContent>

        <CardFooter className="pt-2 flex flex-wrap gap-1">
          {article.tags.map(tag => (
            <span
              key={tag}
              className={`text-xs px-2 py-1 rounded-full transition-colors duration-300 ${isHovering
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700'
                }`}
            >
              {tag}
            </span>
          ))}
        </CardFooter>
      </Card>
    </Link>
  )
}

export function BlogPage() {
  const location = useLocation();
  const isRootBlogPage = location.pathname === '/insights/blog' || location.pathname === '/insights/blog/';

  return (
    <BasePage
      title="Blog"
      description="Insights, opinions, and announcements from our team and partners"
    >
      {isRootBlogPage ? (
        <div className="space-y-12">
          {/* Featured Article */}
          <div className="relative rounded-xl overflow-hidden shadow-lg h-96">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${articles[0].coverImage})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white">
                  <div
                    className="w-full h-full bg-cover bg-center"
                    style={{ backgroundImage: `url(${articles[0].author.avatar})` }}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium">{articles[0].author.name}</p>
                  <p className="text-xs opacity-80">{articles[0].date}</p>
                </div>
              </div>
              <span className="inline-block bg-white text-black text-xs font-semibold px-3 py-1 rounded-full mb-3">
                {articles[0].category}
              </span>
              <h2 className="text-3xl font-bold mb-3">{articles[0].title}</h2>
              <p className="text-lg opacity-90 mb-4">{articles[0].excerpt}</p>
              <Link
                to={`/article/${articles[0].slug}`}
                className="inline-flex items-center gap-2 text-white bg-polymath hover:bg-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                Read Article
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Article Grid */}
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Latest Articles</h2>
              <div className="flex gap-2">
                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                  All
                </button>
                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                  Opinion
                </button>
                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                  Partner Insights
                </button>
                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                  Releases & Updates
                </button>
                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                  Investment Announcements
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.slice(1).map(article => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <Outlet />
      )}
    </BasePage>
  )
}